if (!customElements.get('quick-add-modal')) {
  // Overlays that existed when the page loaded. Globo Color Swatch only watches those, so
  // overlays created later (load more, filters, re-rendered sections) borrow one of them.
  const loadedModals = new Set();

  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      connectedCallback() {
        if (this.moved) return;
        if (document.readyState !== 'complete') loadedModals.add(this);

        // The cart drawer re-renders its markup, so drop the stale copy of the same
        // overlay (already moved to <body>) instead of stacking duplicates.
        if (this.id) {
          document.body.querySelectorAll(`:scope > quick-add-modal[id="${this.id}"]`).forEach((staleModal) => {
            if (staleModal !== this && !staleModal.hasAttribute('open')) staleModal.remove();
          });
        }

        // ModalDialog reads the parent section id, but the cart drawer lives outside any section.
        if (this.closest('.shopify-section')) {
          super.connectedCallback();
        } else {
          this.moved = true;
          document.body.appendChild(this);
        }
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();

        // ModalDialog unlocks the page scroll; keep it locked while the cart drawer is open underneath.
        if (document.querySelector('cart-drawer.active')) document.body.classList.add('overflow-hidden');
      }

      show(opener) {
        if (!loadedModals.has(this)) {
          const loadedModal = [...loadedModals].find((modal) => modal.isConnected && !modal.hasAttribute('open'));
          if (loadedModal) return loadedModal.show(opener);
        }

        if (opener.getAttribute('aria-disabled') === 'true') return;

        const productUrl = opener.getAttribute('data-product-url');
        const spinner = opener.querySelector('.loading__spinner');

        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        spinner?.classList.remove('hidden');

        fetch(productUrl)
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            // Without the product markup there is nothing to show: fall back to the product page.
            if (!productElement) {
              window.location.href = productUrl;
              return;
            }

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
            this.renderProductOptions(productUrl);
          })
          .catch((error) => {
            console.error(error);
            window.location.href = productUrl;
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            spinner?.classList.add('hidden');
          });
      }

      // Globo Product Options (name, engraving...) are normally rendered on the product page only.
      // Its quick view hook renders the same fields into the overlay form.
      renderProductOptions(productUrl) {
        const handle = new URL(productUrl, window.location.origin).pathname.split('/products/')[1]?.split('/')[0];
        if (!handle) return;

        let attempts = 0;
        const render = () => {
          const gpo = window.GPOConfigs;
          if (typeof gpo?.handleQuickViewActivatorClick === 'function') {
            gpo.handleQuickViewActivatorClick(decodeURIComponent(handle), {
              quickViewProductForm: 'quick-add-modal[open] form[data-type="add-to-cart-form"]',
            });
          } else if (++attempts < 20) {
            setTimeout(render, 250);
          }
        };
        render();
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
